// eFiling completeness calculator — pure function for real-time progress tracking
// Walks required fields per tab using field visibility and EFM requirements
import { getCaseCategoryFieldVisibility } from '../efm-reference/case-category-metadata.js';
import { feeWaiverSatisfied, partyRequestsFeeWaiver } from './common/fee-waiver.js';
import { isFeeQuoteResolved, paymentMethodRequired } from './types.js';
const ORDER_INFO_FIELDS = [
    {
        field: 'orderInfo.county',
        label: 'County',
        isRequired: () => true,
        isComplete: (s) => !!s.orderInfo.county,
    },
    {
        field: 'orderInfo.filingType',
        label: 'Filing Type',
        isRequired: () => true,
        isComplete: (s) => !!s.orderInfo.filingType,
    },
    {
        field: 'caseInfo.subcaseSelected',
        label: 'Subcase',
        isRequired: (s) => s.orderInfo.filingType === 'subsequent-filing' &&
            (s.caseInfo.subcaseCount ?? 0) > 1,
        isComplete: (s) => !!s.caseInfo.complaintId,
    },
];
const CASE_INFO_FIELDS = [
    {
        field: 'caseInfo.caseTitle',
        label: 'Case Title',
        isRequired: () => true,
        isComplete: (s) => !!s.caseInfo.caseTitle.trim(),
    },
    {
        field: 'caseInfo.caseCategoryCode',
        label: 'Case Category',
        isRequired: () => true,
        isComplete: (s) => !!s.caseInfo.caseCategoryCode,
    },
    {
        field: 'caseInfo.caseType',
        label: 'Case Type',
        isRequired: () => true,
        isComplete: (s) => s.caseInfo.caseType !== null,
    },
    {
        field: 'caseInfo.courtJurisdiction',
        label: 'Court Jurisdiction',
        isRequired: (s) => {
            if (s.orderInfo.filingType === 'subsequent-filing')
                return false;
            if (!s.caseInfo.caseCategoryCode)
                return false;
            return getCaseCategoryFieldVisibility(s.caseInfo.caseCategoryCode).showCourtJurisdiction;
        },
        isComplete: (s) => s.caseInfo.courtJurisdiction !== null,
    },
    {
        field: 'caseInfo.incidentZip',
        label: 'Incident Zip',
        isRequired: (s) => {
            if (s.orderInfo.filingType === 'subsequent-filing')
                return false;
            if (!s.caseInfo.caseCategoryCode)
                return false;
            return getCaseCategoryFieldVisibility(s.caseInfo.caseCategoryCode).showIncidentZip;
        },
        isComplete: (s) => !!s.caseInfo.incidentZip.trim(),
    },
];
const CASE_PARTICIPANTS_FIELDS = [
    {
        field: 'parties',
        label: 'At least one party',
        isRequired: () => true,
        isComplete: (s) => s.parties.length > 0,
    },
    {
        field: 'parties.leadClient',
        label: 'Lead client designated',
        isRequired: () => true,
        isComplete: (s) => s.parties.filter(p => p.isLeadClient).length === 1,
    },
    {
        field: 'attorneys',
        label: 'Attorney or self-represented party',
        isRequired: () => true,
        isComplete: (s) => s.parties.some(p => p.isSelfRepresented) || s.attorneys.length > 0,
    },
    {
        field: 'parties.orgPartiesHaveAttorney',
        label: 'Organization plaintiffs have attorney',
        isRequired: (s) => {
            const orgPlaintiffRoles = ['PLAIN', 'PET'];
            return s.parties.some(p => p.partyType === 'organization' && orgPlaintiffRoles.includes(p.roleCode));
        },
        isComplete: (s) => {
            const orgPlaintiffRoles = ['PLAIN', 'PET'];
            const orgPlaintiffs = s.parties.filter(p => p.partyType === 'organization' && orgPlaintiffRoles.includes(p.roleCode));
            return orgPlaintiffs.every(party => s.attorneys.some(att => att.representedPartyIds?.includes(party.id)));
        },
    },
];
const DOCUMENTS_FIELDS = [
    {
        field: 'documents',
        label: 'At least one document',
        isRequired: () => true,
        isComplete: (s) => s.documents.length > 0,
    },
    {
        field: 'documents.documentType',
        label: 'Document type selected for all documents',
        isRequired: () => true,
        isComplete: (s) => s.documents.length > 0 && s.documents.every(d => !!d.documentType),
    },
    {
        field: 'documents.feeWaiverFw001',
        label: 'Request to Waive Court Fees document for fee waiver',
        // Row shows whenever a party requests a fee waiver. Completeness delegates to
        // the shared predicate so it honors the same carve-outs as the submit gate and
        // JTI fee-calc: GOVT_ENTITY exemptions never need FW001, and a feeWaiverOnFile
        // (existing-waiver) party is satisfied without a connected FW001 document.
        isRequired: (s) => s.parties.some(partyRequestsFeeWaiver),
        isComplete: (s) => s.parties.every(p => feeWaiverSatisfied(p, s.documents)),
    },
];
function courtFeesFields(showCourtFeesTab) {
    if (!showCourtFeesTab)
        return [];
    return [
        {
            field: 'courtFees.authorizePayment',
            label: 'Authorize court fee payment',
            isRequired: () => true,
            isComplete: (s) => s.courtFees.authorizePayment,
        },
        {
            // SPEC-SOR-343 — always required, and complete exactly when the fee
            // quote resolved, so every non-resolved state (absent, unresolved,
            // degraded, failed) reads incomplete on the classic Submit gate for the
            // fee-calculation reason — even with `authorizePayment` persisted true
            // and no card on file. The same decider the validator's
            // fee-quote-not-resolved rule consumes.
            field: 'courtFees.quoteState',
            label: 'Fee calculation',
            isRequired: () => true,
            isComplete: (s) => isFeeQuoteResolved(s.courtFees),
        },
    ];
}
const ORDER_DETAILS_FIELDS = [
    {
        field: 'orderDetails.paymentMethodId',
        label: 'Payment Method',
        // SPEC-SOR-343 — the card is required exactly when the resolved total is
        // non-zero, so a resolved $0.00 total (a waived filing, FWR-14) drops it
        // from the required set. Same decider the whole-order payment validator
        // consumes.
        isRequired: (s) => paymentMethodRequired(s.courtFees),
        isComplete: (s) => !!s.orderDetails.paymentMethodId,
    },
];
// ---------------------------------------------------------------------------
// EFM-specific field definitions from requirements
// ---------------------------------------------------------------------------
function efmRequirementFields(requirements) {
    if (!requirements)
        return [];
    const fields = [];
    if (requirements.requireLeadDocument) {
        fields.push({
            field: 'documents.leadDocument',
            label: 'Lead document designated',
            isRequired: () => true,
            isComplete: (s) => s.documents.some(d => d.isLeadDocument),
        });
    }
    return fields;
}
const SECTIONS = [
    {
        id: 'order-info',
        label: 'Order Info',
        getFields: () => ORDER_INFO_FIELDS,
    },
    {
        id: 'case-details',
        label: 'Case Details',
        getFields: () => [...CASE_INFO_FIELDS, ...CASE_PARTICIPANTS_FIELDS],
    },
    {
        id: 'documents',
        label: 'Documents',
        getFields: (_s, opts) => [
            ...DOCUMENTS_FIELDS,
            ...efmRequirementFields(opts.efmRequirements),
        ],
    },
    {
        id: 'court-fees',
        label: 'Court Fees',
        getFields: (_s, opts) => courtFeesFields(opts.showCourtFeesTab),
    },
    {
        id: 'order-details',
        label: 'Order Details',
        getFields: () => ORDER_DETAILS_FIELDS,
    },
];
// ---------------------------------------------------------------------------
// Core calculator
// ---------------------------------------------------------------------------
function evaluateField(def, state, section) {
    if (!def.isRequired(state))
        return null;
    const complete = def.isComplete(state);
    const status = complete ? 'complete' : 'incomplete';
    return {
        field: def.field,
        label: def.label,
        section,
        status,
    };
}
function calculateSectionCompleteness(sectionDef, state, options) {
    const fieldDefs = sectionDef.getFields(state, options);
    const fields = [];
    for (const def of fieldDefs) {
        const result = evaluateField(def, state, sectionDef.id);
        if (result)
            fields.push(result);
    }
    const fieldCount = fields.length;
    const completedCount = fields.filter(f => f.status === 'complete').length;
    const errorCount = fields.filter(f => f.status === 'error').length;
    const warningCount = fields.filter(f => f.status === 'warning').length;
    return {
        section: {
            section: sectionDef.id,
            label: sectionDef.label,
            fieldCount,
            completedCount,
            percentage: fieldCount === 0 ? 100 : Math.round((completedCount / fieldCount) * 100),
            errorCount,
            warningCount,
        },
        fields,
    };
}
/**
 * Calculate field-level and section-level completeness for an eFiling order.
 *
 * Pure function — no side effects, safe for useMemo.
 * Consumes EFM requirements to include county-specific fields.
 */
export function calculateCompleteness(state, efmRequirements) {
    // Court fees always apply to an e-filing order (SOR-155 removed the Federal path).
    const showCourtFeesTab = true;
    const options = { showCourtFeesTab, efmRequirements };
    const allFields = [];
    const sections = [];
    for (const sectionDef of SECTIONS) {
        // Skip court fees section if not applicable
        if (sectionDef.id === 'court-fees' && !showCourtFeesTab)
            continue;
        const result = calculateSectionCompleteness(sectionDef, state, options);
        allFields.push(...result.fields);
        sections.push(result.section);
    }
    const totalFields = allFields.length;
    const completedFields = allFields.filter(f => f.status === 'complete').length;
    return {
        totalFields,
        completedFields,
        percentage: totalFields === 0 ? 100 : Math.round((completedFields / totalFields) * 100),
        fields: allFields,
        sections,
    };
}
